import { useMemo } from 'react';

import formatDistanceToNowStrict from 'date-fns/formatDistanceToNowStrict';
import parseISO from 'date-fns/parseISO';
import { ptBR } from 'date-fns/locale';
import { FiClock, FiHeart } from 'react-icons/fi';

import { VideosData } from '@common/types/api';
import { useAuth, useVideoLikeMutation } from '@hooks';
import { selectUserId } from '@hooks/useAuth/selectors';

import VideoCommentSection from '../VideoCommentSection';
import VideoDescription from './VideoDescription';
import * as S from './styles';

type VideoInfoProps = {
  video: VideosData;
};

const VideoInfo = ({ video }: VideoInfoProps) => {
  const userId = useAuth(selectUserId);

  const userHasLiked = useMemo(
    () => !!video.likes?.data.some((like) => like.user_id === userId),
    [video.likes, userId],
  );

  const { mutate: toggleLike } = useVideoLikeMutation(video.id, userHasLiked);

  const timeAgo = useMemo(() => {
    if (!video.created_at) return null;
    return formatDistanceToNowStrict(parseISO(video.created_at), {
      locale: ptBR,
      addSuffix: true,
    });
  }, [video.created_at]);

  const authorInitials = useMemo(() => {
    if (!video.author?.name) return '';
    return video.author.name
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0].toUpperCase())
      .join('');
  }, [video.author?.name]);

  return (
    <>
      <S.InfoCard>
        {video.categories?.data && video.categories.data.length > 0 && (
          <S.TagsRow>
            {video.categories.data.map((cat) => (
              <S.Tag key={cat.id}>{cat.name}</S.Tag>
            ))}
          </S.TagsRow>
        )}

        <S.Title>{video.title}</S.Title>

        <S.MetaRow>
          <S.MetaItem>
            <FiHeart />
            <span>{video.likes_count} curtidas</span>
          </S.MetaItem>
          {timeAgo && (
            <S.MetaItem>
              <FiClock />
              <span>{timeAgo}</span>
            </S.MetaItem>
          )}
          <S.MetaItem>
            <FiHeart />
            <span>{video.likes_count} curtidas</span>
          </S.MetaItem>
        </S.MetaRow>

        <S.Divider />

        <S.AuthorSection>
          {video.author?.name && (
            <S.AuthorLeft>
              <S.AuthorAvatar>{authorInitials}</S.AuthorAvatar>
              <div>
                <S.AuthorName>{video.author.name}</S.AuthorName>
                <S.AuthorRole>Organizador do evento</S.AuthorRole>
              </div>
            </S.AuthorLeft>
          )}

          <S.ActionsBar>
            <S.ActionPill
              type="button"
              $liked={userHasLiked}
              onClick={() => toggleLike()}
            >
              <FiHeart />
              {video.likes_count}
            </S.ActionPill>
          </S.ActionsBar>
        </S.AuthorSection>
      </S.InfoCard>

      {video.description && <VideoDescription text={video.description} />}

      <VideoCommentSection
        videoId={video.id}
        commentsCount={video.comments_count}
      />
    </>
  );
};

export default VideoInfo;
